import {
  Button,
  Col,
  Descriptions,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Switch,
  Table,
  Tag,
} from 'antd';
import {EditTwoTone, UserAddOutlined, InfoCircleOutlined} from '@ant-design/icons';
import {ColumnsType, TablePaginationConfig} from 'antd/lib/table';
import {useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from '../../../../common/hooks';
import {
  employeesList,
  addEmployee,
  updateEmployee,
  pageNumberChange,
  pageSizeChange,
  searchBy,
  employeesStatusFilter,
  sortBy,
} from './employeeSlice';
import type {ADD_EMPLOYEE, UPDATE_EMP, USERDATA} from './employees.type';
import {formatDate} from '../../../../utils/formatDate';
import checkFormUpdate from '../../../../utils/checkFormUpdate';
import {authStationsList, clearStationsFilters} from '../../station/stations/stationsSlice';
import {clearRacksFilters} from '../../rack/racks/racksSlice';
import {sortbyFilter, statusFilter, statusFilterOptions} from '../../../../common/constants';

const Employees = () => {
  const dispatch = useAppDispatch();
  const employees = useAppSelector((state) => state.employees);
  const userPermission = useAppSelector((state) => state.config.config);
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isUpdateModal, setIsUpdateModal] = useState(false);
  const [employeeForm] = Form.useForm();
  const [modalData, setModalData] = useState<USERDATA>({} as USERDATA);

  const handleUpdateUser = (values: UPDATE_EMP) => {
    const pass = values.password;
    delete values.password;
    const payload = checkFormUpdate(values, modalData);
    if (pass !== undefined && pass?.length) {
      payload.password = pass;
    }

    console.log(payload);

    if (Object.keys(payload).length > 0) {
      dispatch(updateEmployee({payload: payload, id: modalData.id}))
        .unwrap()
        .then(() => {
          handleEmployeeModalClose();
          dispatch(
            employeesList({
              page_size: employees.employees.page_size,
              page_number: employees.employees.page_number,
              sort_by: employees.employees.sort_by,
              ascending: employees.employees.ascending,
              search: employees.employees.search,
            }),
          );
        });
    } else {
      message.warning('No fields are updated');
    }
  };

  const handleAddUser = (values: ADD_EMPLOYEE) => {
    //console.log(values);

    dispatch(addEmployee(values))
      .unwrap()
      .then(() => {
        handleEmployeeModalClose();
        dispatch(
          employeesList({
            page_size: employees.employees.page_size,
            page_number: employees.employees.page_number,
            sort_by: employees.employees.sort_by,
            ascending: employees.employees.ascending,
            search: employees.employees.search,
          }),
        );
      });
    // .catch((error) => {});
  };

  const handleEmployeeModalClose = () => {
    employeeForm.resetFields();
    setIsEmployeeModalOpen(false);
    dispatch(clearStationsFilters());
    dispatch(clearRacksFilters());
  };

  useEffect(() => {
    //
    dispatch(
      employeesList({
        page_size: employees.employees.page_size,
        page_number: employees.employees.page_number,
        sort_by: employees.employees.sort_by,
        ascending: employees.employees.ascending,
        search: employees.employees.search,
        is_active: employees.employees.is_active,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    employees.employees.page_number,
    employees.employees.page_size,
    employees.employees.search,
    employees.employees.is_active,
    employees.employees.ascending,
    employees.employees.sort_by,
  ]);

  const columns: ColumnsType<USERDATA> = [
    {
      title: 'S. No.',
      width: 70,
      render: (test, record, index) => {
        return (
          <>{(employees.employees.page_number - 1) * employees.employees.page_size + index + 1}</>
        );
      },
    },
    {
      title: 'Full Name',
      dataIndex: 'name',
      sorter: true,
    },
    {
      title: 'User Name',
      dataIndex: 'username',
      sorter: true,
      render: (text, record) => {
        if (record.is_admin) {
          return (
            <>
              <>{text}</>
              <Tag color='green' style={{marginLeft: '10px'}}>
                Admin
              </Tag>
            </>
          );
        } else {
          return <>{text}</>;
        }
      },
    },
    {
      title: ' Email',
      dataIndex: 'email',
      sorter: true,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
    },

    {
      title: 'Status',
      dataIndex: 'is_active',
      render: (_, record) => {
        return record.is_active === true ? (
          <Tag color='green'>Active</Tag>
        ) : (
          <Tag color='red'>Inactive</Tag>
        );
      },
    },

    {
      title: 'Actions',
      key: 'operation',
      render: (_, record) => {
        return (
          <>
            <InfoCircleOutlined
              style={{cursor: 'pointer', fontSize: '16px', color: 'green', marginInline: '10px'}}
              onClick={() => {
                setModalData(record);
                setIsInfoModalOpen(true);
              }}
            />
            {userPermission.permissions.update_employee ? (
              <EditTwoTone
                style={{cursor: 'pointer', fontSize: '16px', color: 'blue', marginInline: '10px'}}
                onClick={() => {
                  setModalData(record);
                  setIsUpdateModal(true);
                  dispatch(authStationsList({is_active: true}));
                  employeeForm.setFieldsValue({
                    name: record.name,
                    email: record.email,
                    phone: record.phone,
                    is_active: record?.is_active,
                    password: undefined,
                    is_admin: record.is_admin,
                    is_station_employee: record.is_station_employee,
                  });
                  setIsEmployeeModalOpen(true);
                }}
              />
            ) : null}
          </>
        );
      },
    },
  ];

  const handleSearch = (e: string) => {
    if (e.length > 3) {
      dispatch(searchBy(e));
    } else if (e.length === 0) {
      dispatch(searchBy(e));
    }
  };

  return (
    <div style={{paddingInline: '20px'}}>
      <div
        style={{
          margin: '10px 0px 10px 0px',
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '350px 1fr 1fr',
        }}
      >
        <Input.Search
          placeholder='Search'
          defaultValue={employees.employees.search}
          style={{borderRadius: '10px'}}
          allowClear
          onSearch={(e) => handleSearch(e)}
        />
        <Select
          style={{width: '100px', marginLeft: '5px'}}
          value={
            employees.employees.is_active === undefined
              ? statusFilter.ALL
              : employees.employees.is_active === true
              ? statusFilter.ACTIVE
              : statusFilter.INACTIVE
          }
          options={statusFilterOptions}
          onChange={(value) => {
            if (value === statusFilter.ACTIVE) {
              dispatch(employeesStatusFilter(true));
            } else if (value === statusFilter.ALL) {
              dispatch(employeesStatusFilter(undefined));
            } else {
              dispatch(employeesStatusFilter(false));
            }
          }}
          showArrow
        />
        <div style={{width: '100%', textAlign: 'end'}}>
          {userPermission.permissions.add_employee ? (
            <Button
              type='primary'
              icon={<UserAddOutlined />}
              style={{borderRadius: '5px', marginInline: '5px'}}
              onClick={() => {
                setIsUpdateModal(false);
                dispatch(authStationsList({is_active: true}));
                employeeForm.setFieldsValue({is_station_employee: false});
                setIsEmployeeModalOpen(true);
              }}
            >
              Add Employee
            </Button>
          ) : null}
        </div>
      </div>
      <Table
        rowKey='id'
        columns={columns}
        pagination={
          {
            total: employees.employees.totalDataCount,
            current: employees.employees.page_number,
            pageSize: employees.employees.page_size,
            showSizeChanger: true,
            // onChange: (pageNumber, pageSize) => {
            //   if (pageSize !== employees.employees.page_size) {
            //     dispatch(pageSizeChange({pageNumber, pageSize}));
            //   } else {
            //     dispatch(pageNumberChange(pageNumber));
            //   }
            // },
          } as TablePaginationConfig
        }
        loading={employees.isLoading}
        size='middle'
        dataSource={employees.employees.pageData}
        scroll={{x: undefined, y: '60vh'}}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={(pagination, _, {order, field}: any) => {
          if (pagination.pageSize && pagination.pageSize !== employees.employees.page_size) {
            dispatch(pageSizeChange(pagination.pageSize));
          }
          if (pagination.current && pagination.current !== employees.employees.page_number) {
            dispatch(pageNumberChange(pagination.current));
          }
          if (field) {
            if (order === sortbyFilter.DESCEND) {
              dispatch(sortBy({ascending: false, sortBy: field}));
            } else {
              dispatch(sortBy({ascending: true, sortBy: field}));
            }
          }
        }}
      />
      <Modal
        title={`${modalData.name}`}
        open={isInfoModalOpen}
        onCancel={() => {
          setIsInfoModalOpen(false);
        }}
        footer={null}
        width={800}
      >
        <Descriptions bordered column={2}>
          <Descriptions.Item label='Employee Name' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.name}
          </Descriptions.Item>
          <Descriptions.Item label='User Name' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.username}
          </Descriptions.Item>
          <Descriptions.Item label='Email' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.email}
          </Descriptions.Item>
          <Descriptions.Item label='Phone' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.phone}
          </Descriptions.Item>
          <Descriptions.Item label='Status' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.is_active ? (
              <Tag color='green'>Active</Tag>
            ) : (
              <Tag color='red'>Inactive</Tag>
            )}
          </Descriptions.Item>

          <Descriptions.Item label='Is Admin' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.is_admin ? <Tag color='green'>Yes</Tag> : <Tag color='red'>No</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label='Created By' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.created_by}
          </Descriptions.Item>
          <Descriptions.Item label='Created At' labelStyle={{fontWeight: 'bolder'}}>
            {formatDate(modalData.created_at)}
          </Descriptions.Item>
          <Descriptions.Item label='Updated By' labelStyle={{fontWeight: 'bolder'}}>
            {modalData.updated_by}
          </Descriptions.Item>
          <Descriptions.Item label='Updated At' labelStyle={{fontWeight: 'bolder'}}>
            {formatDate(modalData.updated_at)}
          </Descriptions.Item>
          <Descriptions.Item label='Last Login ' labelStyle={{fontWeight: 'bolder'}}>
            {formatDate(modalData.last_login_at)}
          </Descriptions.Item>
        </Descriptions>
      </Modal>
      <Modal
        title={isUpdateModal ? 'Update Employee' : 'Add Employee'}
        open={isEmployeeModalOpen}
        maskClosable={false}
        onCancel={() => {
          handleEmployeeModalClose();
        }}
        okText={isUpdateModal ? 'Update ' : 'Add'}
        confirmLoading={employees.isLoading}
        onOk={() => {
          employeeForm
            .validateFields()
            .then((values) => {
              //employeeForm.resetFields();
              {
                isUpdateModal ? handleUpdateUser(values) : handleAddUser(values);
              }
            })
            .catch((info) => {
              console.error('Validate Failed:', info);
            });
        }}
      >
        <Row>
          <Col span={24}>
            <Form
              name='employeeForm'
              form={employeeForm}
              labelCol={{span: 8}}
              wrapperCol={{span: 14}}
              layout='horizontal'
            >
              <Form.Item
                name='name'
                label='Full Name'
                rules={[
                  {
                    required: true,
                    message: 'Please fill your Username ',
                  },
                  {min: 4, max: 50, message: ' Usename should be between 4-50 characters'},
                ]}
              >
                <Input autoComplete='off' placeholder='Full Name' />
              </Form.Item>
              {!isUpdateModal && (
                <Form.Item
                  name='username'
                  label=' User Name'
                  rules={[
                    {
                      required: true,
                      message: 'Please fill your Username ',
                    },
                    {min: 4, max: 16, message: ' Usename should be between 4-16 characters'},
                  ]}
                >
                  <Input autoComplete='off' placeholder='Username' />
                </Form.Item>
              )}
              <Form.Item
                name='password'
                label='Password'
                rules={[
                  {
                    min: 4,
                    max: 16,
                    required: isUpdateModal ? false : true,
                    message: 'Password must be between 8 to 16 characters',
                  },
                ]}
              >
                <Input.Password autoComplete='off' placeholder='Password' />
              </Form.Item>

              <Form.Item
                name='email'
                label='Email'
                rules={[
                  {
                    type: 'email',
                    required: true,
                    message: 'Please fill a valid email',
                  },
                ]}
              >
                <Input autoComplete='off' placeholder='Email' />
              </Form.Item>
              <Form.Item
                name='phone'
                label='Phone'
                rules={[
                  {
                    pattern: new RegExp('^[0-9]{10}$'),
                    required: true,
                    message: 'Enter a valid 10 digit number',
                  },
                ]}
              >
                <Input autoComplete='off' placeholder='Phone' />
              </Form.Item>

              {isUpdateModal && (
                <Form.Item name='is_active' label='Status' valuePropName='checked'>
                  <Switch checkedChildren='Active' unCheckedChildren='Inactive' />
                </Form.Item>
              )}
              {userPermission.is_admin && isUpdateModal && (
                <Form.Item name='is_admin' label='Admin' valuePropName='checked'>
                  <Switch checkedChildren='Yes' unCheckedChildren='No' />
                </Form.Item>
              )}
              <Form.Item
                name='is_station_employee'
                label=' Station Employee'
                valuePropName='checked'
              >
                <Switch checkedChildren='Yes' unCheckedChildren='No' />
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default Employees;
